import {
  ChoiceChips,
  Combobox,
  IconButton,
  Input,
  SearchBox,
  Select,
  type ChoiceTone,
} from '@platform/design-system';
import { Keyboard } from 'lucide-react';
import { companyFilterOptionsOf, type Companies } from '../../companies.ts';
import { useEffect, useState, type Ref } from 'react';
import { useDebouncedValue } from '../../useDebouncedValue.ts';
import { usePageContext } from '../../usePageContext.ts';
import { nameIn, type Category } from '../categories.ts';
import type { OptionValueChoice } from '../options.ts';
import {
  countOfView,
  PRODUCT_SORTS,
  PRODUCT_VIEWS,
  viewOf,
  withView,
  type ProductCounts,
  type ProductFilters,
  type ProductView,
} from './product-filters.ts';

import { OPERATION_PERIODS } from './operation-period.ts';
import styles from './ProductList.module.css';

export type ProductViewChipsProps = {
  filters: ProductFilters;
  counts: ProductCounts | undefined;
  onFiltersChange: (filters: ProductFilters) => void;
};

export type ProductListToolsProps = {
  filters: ProductFilters;
  categories: readonly Category[];
  optionValues: readonly OptionValueChoice[];
  companies: Companies;
  searchRef: Ref<HTMLInputElement>;
  searchBoxKey: number;
  onFiltersChange: (filters: ProductFilters) => void;
  onSearch: (text: string) => void;
  onSearchLeave: (text: string) => void;
  onShowShortcuts: () => void;
};

const VIEW_TONES: Readonly<Partial<Record<ProductView, ChoiceTone>>> = { low: 'warning', out: 'danger' };

const SEARCH_SHORTCUT = '/';

const GROUP_STARTS: ReadonlySet<ProductView> = new Set(['low', 'sale']);

const SEARCH_DELAY_MS = 400;

type ProductSearchProps = {
  initial: string;
  searchRef: Ref<HTMLInputElement>;
  onSearch: (text: string) => void;
  onSearchText: (text: string) => void;
};

const ProductSearch = ({ initial, searchRef, onSearch, onSearchText }: ProductSearchProps) => {
  const { t } = usePageContext();
  const [text, setText] = useState(initial);
  const settled = useDebouncedValue(text, SEARCH_DELAY_MS);

  useEffect(() => {
    if (settled !== initial) onSearchText(settled);
  }, [settled, initial, onSearchText]);

  return (
    <SearchBox
      ref={searchRef}
      label={t('catalog.search')}
      placeholder={t('catalog.searchPlaceholder')}
      shortcut={SEARCH_SHORTCUT}
      submitLabel={t('catalog.searchSubmit')}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onSearch={onSearch}
      onBlur={(event) => {
        onSearchText(event.target.value);
      }}
    />
  );
};

const dayOrNull = (text: string): string | null => (text === '' ? null : text);

const OperationPeriodPicker = ({ filters, onFiltersChange }: Omit<ProductViewChipsProps, 'counts'>) => {
  const { t } = usePageContext();
  return (
    <div className={styles['periodPicker']}>
      <Select
        label={t('productList.operationPeriod')}
        hideLabel
        value={filters.operationPeriod}
        options={OPERATION_PERIODS.map((period) => ({
          value: period,
          label: t(`productList.period_${period}`),
        }))}
        onChange={(event) => {
          const operationPeriod = OPERATION_PERIODS.find((period) => period === event.target.value);
          if (operationPeriod !== undefined) onFiltersChange({ ...filters, operationPeriod });
        }}
      />
      {filters.operationPeriod === 'custom' ? (
        <>
          <Input
            type="date"
            label={t('productList.periodFrom')}
            hideLabel
            value={filters.operationFrom ?? ''}
            max={filters.operationTo ?? undefined}
            onChange={(event) => {
              onFiltersChange({ ...filters, operationFrom: dayOrNull(event.target.value) });
            }}
          />
          <Input
            type="date"
            label={t('productList.periodTo')}
            hideLabel
            value={filters.operationTo ?? ''}
            min={filters.operationFrom ?? undefined}
            onChange={(event) => {
              onFiltersChange({ ...filters, operationTo: dayOrNull(event.target.value) });
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export const ProductViewChips = ({ filters, counts, onFiltersChange }: ProductViewChipsProps) => {
  const { t, locale } = usePageContext();
  return (
    <ChoiceChips
      trailing={<OperationPeriodPicker filters={filters} onFiltersChange={onFiltersChange} />}
      legend={t('productList.view')}
      hideLegend
      locale={locale}
      value={viewOf(filters)}
      choices={PRODUCT_VIEWS.map((view) => ({
        value: view,
        label: t(`productList.view_${view}`),
        count: countOfView(counts, view),
        tone: VIEW_TONES[view],
        startsGroup: GROUP_STARTS.has(view),
      }))}
      onChange={(view) => {
        onFiltersChange(withView(filters, view));
      }}
    />
  );
};

export const ProductListTools = ({
  filters,
  categories,
  optionValues,
  companies,
  searchRef,
  searchBoxKey,
  onFiltersChange,
  onSearch,
  onSearchLeave,
  onShowShortcuts,
}: ProductListToolsProps) => {
  const { t, locale } = usePageContext();

  return (
    <div className={styles['listTools']}>
      <ProductSearch
        key={searchBoxKey}
        initial={filters.search ?? ''}
        searchRef={searchRef}
        onSearch={onSearch}
        onSearchText={onSearchLeave}
      />
      <Combobox
        label={t('catalog.category')}
        hideLabel
        value={filters.categoryId ?? ''}
        searchPlaceholder={t('productList.findCategory')}
        noMatchLabel={t('productList.noCategoryMatch')}
        options={[
          { value: '', label: t('productList.allCategories') },
          ...categories.map((category) => ({
            value: category.id,
            label: nameIn(category, locale, category.id),
          })),
        ]}
        onChange={(categoryId) => {
          onFiltersChange({ ...filters, categoryId: categoryId === '' ? null : categoryId });
        }}
      />
      {optionValues.length === 0 ? null : (
        <Combobox
          label={t('productList.optionValue')}
          hideLabel
          value={filters.optionValueId ?? ''}
          searchPlaceholder={t('productList.findOptionValue')}
          noMatchLabel={t('productList.noOptionValueMatch')}
          options={[{ value: '', label: t('productList.allOptionValues') }, ...optionValues]}
          onChange={(optionValueId) => {
            onFiltersChange({ ...filters, optionValueId: optionValueId === '' ? null : optionValueId });
          }}
        />
      )}
      {companies.subsidiaries.length === 0 ? null : (
        <Select
          label={t('companies.label')}
          hideLabel
          value={filters.company ?? ''}
          options={[{ value: '', label: t('companies.all') }, ...companyFilterOptionsOf(companies)]}
          onChange={(event) => {
            onFiltersChange({ ...filters, company: event.target.value === '' ? null : event.target.value });
          }}
        />
      )}
      <Select
        label={t('productList.sort')}
        hideLabel
        value={filters.sort}
        options={PRODUCT_SORTS.map((sort) => ({
          value: sort,
          label: t(`productList.sort_${sort.replace(':', '_')}`),
        }))}
        onChange={(event) => {
          const sort = PRODUCT_SORTS.find((candidate) => candidate === event.target.value);
          if (sort !== undefined) onFiltersChange({ ...filters, sort });
        }}
      />
      <IconButton
        label={t('shortcuts.title')}
        icon={<Keyboard size={20} aria-hidden="true" />}
        onClick={onShowShortcuts}
      />
    </div>
  );
};
